import { ICourseModulesNodes } from '@dtos/Course'
import { useGetModuleCourseContents } from '@services/api/graphql/moduleCourseContents'
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface ICourseContents {
  course: {
    courseModules: {
      nodes: ICourseModulesNodes[]
    } | null
  }
}

interface ICourseContentsProviderProps {
  isLoading: boolean
  error?: Error
  getCourseContents?: (
    moduleId: string,
    offset: number,
  ) => Promise<ICourseContents>
}

export const CourseContentsContext =
  createContext<ICourseContentsProviderProps>(
    {} as ICourseContentsProviderProps,
  )

export function CourseContentsProvider({ children }: { children: ReactNode }) {
  const showApolloErrorToast = useApolloErrorToast()
  const { courseId } = useCourseRouteParams()

  const { getModuleCourseContents, loading, error } =
    useGetModuleCourseContents({
      id: `${courseId}`,
      moduleId: '',
      offset: 0,
      limit: 10,
    })

  useEffect(() => {
    if (error && error.message !== 'Empty page!') {
      showApolloErrorToast(error, 'apolloErrors.lessonExtraContents.title')
    }
  }, [error, showApolloErrorToast])

  const getCourseContents = useCallback(
    async (moduleId: string, offset: number) => {
      try {
        const { data: courseContentsData } = await getModuleCourseContents({
          variables: {
            moduleId,
            offset,
          },
        })
        return courseContentsData
      } catch (err) {
        showApolloErrorToast(err, 'apolloErrors.lessonExtraContents.title')
      }
    },
    [getModuleCourseContents, showApolloErrorToast],
  )

  const contextValue = useMemo(
    () => ({
      isLoading: loading,
      error,
      getCourseContents,
    }),
    [loading, error, getCourseContents],
  )

  return (
    <CourseContentsContext.Provider value={contextValue}>
      {children}
    </CourseContentsContext.Provider>
  )
}
